import type {DashboardColumnKey} from '../constants/dashboardColumns';
import type {DashboardDateField} from '../hooks/useDashboardFilters';
import type {DashboardFilterState, DateRange} from '../hooks/useDashboardFilterPanel';

const DATE_FIELD_SLUGS: Record<DashboardDateField, string> = {
	created_at: 'created',
	last_activity_at: 'last-activity',
	resolved_at: 'resolved',
};

/**
 * The export route on the graphql server, which holds the session and gates the
 * permission, and streams the bytes on from the ticketing backend.
 *
 * Deliberately not the ticketing gateway: it proxies only `/internal/*`, `/api`
 * and `/healthz`, so any other path falls through to its SPA fallback and answers
 * 200 `text/html`, which is how a CSV download silently becomes an HTML file.
 *
 * Written WITHOUT a leading slash, and that is load-bearing rather than
 * cosmetic. The graphql server is a different origin from the one serving the
 * SPA, and the SPA's static server answers every path it does not recognise with
 * `index.html` at 200 `text/html` — so a root-relative URL resolves against the
 * SPA, and the browser saves the app shell under the `.csv` name: a download that
 * reports success and contains the wrong bytes. Omitting the slash makes an
 * un-prefixed path an obviously broken URL rather than a silently wrong one, and
 * forces callers through the origin helper — the convention the other
 * authenticated asset URLs follow (`file_stream/`, `clientLogo/`, and
 * `attachmentProxyPath`). Build the absolute URL with
 * `ticketingTicketExportUrl` from the graphql seam.
 */
export const TICKET_EXPORT_PATH = 'ticketing-ticket-export.csv';

export function dashboardCsvFilename({from, to, dateField}: {from: string; to: string; dateField: DashboardDateField}): string {
	return `ticket-dashboard-${DATE_FIELD_SLUGS[dateField]}-${from}-to-${to}.csv`;
}

/**
 * Maps the dashboard's frontend column keys to the export endpoint's column
 * param names. Keys absent from this map have no backing export column and are
 * silently dropped rather than forwarded — an entirely-unknown set would cause
 * the backend to fall back to its DEFAULT_COLUMNS, which would produce a file
 * whose columns bear no relation to what the user was looking at.
 */
export const EXPORT_COLUMN_KEY: Partial<Record<DashboardColumnKey, string>> = {
	id: 'id',
	subject: 'subject',
	requester: 'requester',
	assignee: 'assignee',
	status: 'status',
	priority: 'priority',
	channel: 'channel',
	created: 'created_at',
	resolution_time: 'resolution_time_seconds',
	first_response_time: 'first_response_time_seconds',
	last_activity_at: 'last_activity_at',
	resolved_at: 'resolved_at',
	watchers_count: 'watchers_count',
	// Unmapped (no backing export column): contact, project, tags, sla_state,
	// sla_due_at, sla_breached_at, sla_paused_at, time_in_current_status,
	// sla_policy_name
};

/**
 * Converts an ordered list of visible frontend column keys into the ordered
 * export column param values, dropping any keys that have no export equivalent.
 */
export function toExportColumns(columnKeys: readonly DashboardColumnKey[]): string[] {
	const result: string[] = [];
	for (const key of columnKeys) {
		const exportKey = EXPORT_COLUMN_KEY[key];
		if (exportKey !== undefined) {
			result.push(exportKey);
		}
	}
	return result;
}

/** Backend query-string keys for each date field's range pair. */
const DATE_FIELD_PARAM_KEYS: Record<DashboardDateField, {after: string; before: string}> = {
	created_at: {after: 'created_after', before: 'created_before'},
	last_activity_at: {after: 'last_activity_after', before: 'last_activity_before'},
	resolved_at: {after: 'resolved_after', before: 'resolved_before'},
};

/**
 * Intersect a range already on `params` with a further one: the later `from`
 * and the earlier `to` win, so a panel range on the same field the toolbar is
 * on narrows rather than overwrites it. Mirrors the graphql builders.
 */
function narrowParamRange(params: URLSearchParams, keys: {after: string; before: string}, from: string, to: string): void {
	if (from) {
		const existing = params.get(keys.after);
		params.set(keys.after, existing != null && existing > from ? existing : from);
	}
	if (to) {
		const existing = params.get(keys.before);
		params.set(keys.before, existing != null && existing < to ? existing : to);
	}
}

function applyPanelRange(params: URLSearchParams, dateField: DashboardDateField, range: DateRange): void {
	if (!range.from && !range.to) return;
	narrowParamRange(params, DATE_FIELD_PARAM_KEYS[dateField], range.from, range.to);
}

/**
 * Builds the URLSearchParams for the export endpoint, mirroring the param names
 * that the backend's shared ticket-filter parser reads. Each array field is
 * appended as repeated params (never comma-joined) because that parser does not
 * split on commas: it validates each entry against the status/priority/channel
 * vocabulary and drops what it does not recognise, so `status=open,pending`
 * would leave the export with no status filter at all — a file containing
 * tickets the table on screen excluded, with nothing reporting an error.
 *
 * The fields serialized here are exactly the ones the table's own query sends
 * (see `buildDashboardTicketFilters`), so the file can never describe a
 * different set than the table it was taken from — including the panel's
 * last-activity, resolved-date and first-response ranges, which each get
 * their own param pair and compose with the toolbar range (intersecting when
 * they target the same field).
 *
 * The optional `filename` param is forwarded so the server can set a
 * descriptive Content-Disposition name carrying the range and date field.
 */
export function buildExportParams(
	applied: DashboardFilterState,
	from: string,
	to: string,
	dateField: DashboardDateField,
	columnKeys: readonly DashboardColumnKey[],
	filename?: string
): URLSearchParams {
	const params = new URLSearchParams();

	for (const v of applied.status) params.append('status', v);
	for (const v of applied.priority) params.append('priority', v);
	for (const v of applied.channel) params.append('channel', v);
	for (const v of applied.assignee) params.append('assignee_id', v);
	for (const v of applied.tags) params.append('label_id', v);
	for (const v of applied.requester) params.append('requester_id', v);
	for (const v of applied.slaState) params.append('sla_status', v);

	if (from || to) {
		narrowParamRange(params, DATE_FIELD_PARAM_KEYS[dateField], from, to);
	}

	applyPanelRange(params, 'last_activity_at', applied.lastActivityRange);
	applyPanelRange(params, 'resolved_at', applied.resolvedDateRange);

	if (applied.firstResponseRange.from) params.set('first_response_after', applied.firstResponseRange.from);
	if (applied.firstResponseRange.to) params.set('first_response_before', applied.firstResponseRange.to);

	for (const exportKey of toExportColumns(columnKeys)) {
		params.append('columns', exportKey);
	}

	if (filename) {
		params.set('filename', filename);
	}

	return params;
}

/**
 * The export path and query for an already-serialized param set, origin-relative
 * — see `TICKET_EXPORT_PATH` for why the origin is the caller's job.
 */
export function dashboardExportUrl(params: URLSearchParams): string {
	return `${TICKET_EXPORT_PATH}?${params.toString()}`;
}

/**
 * Starts the streaming export by clicking a hidden anchor. `url` must be absolute
 * (see `dashboardExportUrl`).
 *
 * An anchor rather than a `fetch`: fetching the file would buffer the whole thing
 * in memory, which is the size ceiling this export exists to remove.
 *
 * What actually makes it a download is the response's `Content-Disposition`, not
 * the `download` attribute: the URL is cross-origin, and browsers ignore
 * `download` on a cross-origin anchor. The attribute is still set because it is
 * what names the file on any same-origin deployment, and it costs nothing where
 * it is ignored.
 *
 * That leaves the failure path, which is why this opens a new browsing context. A
 * failure answers a bare status with an empty or JSON body, and because the click
 * is a real navigation, that body would land in whichever tab issued it — blanking
 * the dashboard. Sending it to a new context confines a failure to a throwaway
 * tab; a success never paints one, since the response is an attachment. This
 * mirrors how attachment links open. `noopener` keeps the new context from holding
 * a handle on the dashboard's window.
 *
 * The anchor is removed immediately: the click has already handed the navigation
 * to the browser, which owns the download from that point on.
 */
export function triggerExportDownload(url: string, filename: string): void {
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	anchor.target = '_blank';
	anchor.rel = 'noopener noreferrer';
	anchor.style.display = 'none';
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
}
